import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { AuthContextType, AuthState, User } from '../types/auth';
import { authService } from '../services/authService';

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [authState, setAuthState] = useState<AuthState>({
    isAuthenticated: false,
    user: null,
    token: null,
    loading: true,
  });

  const checkAuth = async () => {
    let token = authService.getToken();
    
    // If no local token, check for cross-subdomain authentication
    if (!token) {
      const foundCrossSubdomainAuth = authService.checkCrossSubdomainAuth();
      if (foundCrossSubdomainAuth) {
        token = authService.getToken(); // Get the token that was set from cookie
        console.log("Using cross-subdomain authentication");
      }
    }
    
    if (!token) {
      setAuthState({
        isAuthenticated: false,
        user: null,
        token: null,
        loading: false,
      });
      return;
    }

    try {
      const user = await authService.getUserInfo();
      if (user) {
        setAuthState({
          isAuthenticated: true,
          user,
          token,
          loading: false,
        });
        
        // Clear the temporary cross-subdomain cookie now that we have valid authentication
        const cookieManager = authService.getCookieManager();
        cookieManager.clearCrossSubdomainAuthCookie();
      } else {
        // Token is invalid, try to refresh
        const newToken = await authService.refreshToken();
        if (newToken) {
          const refreshedUser = await authService.getUserInfo();
          setAuthState({
            isAuthenticated: !!refreshedUser,
            user: refreshedUser,
            token: newToken,
            loading: false,
          });
          
          // Clear the temporary cross-subdomain cookie after successful refresh
          if (refreshedUser) {
            const cookieManager = authService.getCookieManager();
            cookieManager.clearCrossSubdomainAuthCookie();
          }
        } else {
          setAuthState({
            isAuthenticated: false,
            user: null,
            token: null,
            loading: false,
          });
        }
      }
    } catch (error) {
      // console.error('Auth check failed:', error);
      setAuthState({
        isAuthenticated: false,
        user: null,
        token: null,
        loading: false,
      });
    }
  };

  const login = () => {
    authService.redirectToLogin();
  };

  const logout = async () => {
    const refreshToken = localStorage.getItem('bri_refresh_token');
    await authService.logout(refreshToken);
    setAuthState({
      isAuthenticated: false,
      user: null,
      token: null,
      loading: false,
    });
    // Redirect to home page after logout
    window.location.href = '/';
  };

  const refreshToken = async (): Promise<string | null> => {
    const newToken = await authService.refreshToken();
    if (newToken) {
      const user = await authService.getUserInfo();
      setAuthState(prev => ({
        ...prev,
        token: newToken,
        user,
        isAuthenticated: !!user,
      }));
    }
    return newToken;
  };

  useEffect(() => {
    checkAuth();
  }, []);

  // Set up token refresh interval
  useEffect(() => {
    if (authState.isAuthenticated && authState.token) {
      const interval = setInterval(async () => {
        await refreshToken();
      }, 15 * 60 * 1000); // Refresh every 15 minutes

      return () => clearInterval(interval);
    }
  }, [authState.isAuthenticated, authState.token]);

  const contextValue: AuthContextType = {
    ...authState,
    login,
    logout,
    refreshToken,
    checkAuth,
  };

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}; 